import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

// ── Types (mirror backend/app/features/cro_forecast/schemas.py) ───────────

export interface StageBucket {
  stage_name: string;
  deal_count: number;
  amount_cents: number;
}

export interface PipelineBannerData {
  pipeline_name: string;
  total_amount_cents: number;
  total_deal_count: number;
  avg_deal_cents: number;
  /** The stages the banner renders as tiles (Sales shows only SQ/TV/CN). */
  stages: StageBucket[];
  /** The deals behind this banner's total — the tile drill-downs' contents. */
  deals?: OpenDeal[];
  /**
   * Count + amount for every stage present in `deals`, not just the tiled ones.
   * The tiled stages don't sum to the banner total (Sales omits Verbal Commit),
   * so the drawer header uses this to show where the rest sits.
   */
  stage_rollup?: StageBucket[];
  /**
   * Which stage rule produced the totals: `sqo_on` (Sales Pipeline — excludes
   * pre-qualified stages) or `all_open_stages` (everything else). Absent on
   * snapshots taken before the marker existed; the BE treats that as
   * `all_open_stages` and suppresses the WoW chip across the change.
   */
  total_basis?: "sqo_on" | "all_open_stages" | null;
}

export interface OpenDeal {
  hubspot_deal_id: string;
  name: string;
  pipeline_name: string;
  stage_name: string;
  amount_cents: number | null;
  owner_rep_id?: string | null;
  owner_display_name?: string | null;
  hubspot_owner_id?: string | null;
  close_date?: string | null;
  deal_source?: string | null;
  is_strategic?: boolean;
  commit_probability?: number | null;
  commit_category?: string | null;
  days_in_current_stage?: number | null;
  /** ISO date the deal was opened (date-only, no timezone to reconcile). */
  created_date?: string | null;
  /**
   * False for pre-SQO deals, which are excluded from open-pipeline totals. Deal
   * lists show every open deal, so this is how a row that isn't behind the
   * number gets distinguished from one that is. Defaults true when absent.
   */
  counts_toward_pipeline?: boolean;
  /** Pushed bucket only: ISO timestamp of the last time the deal was
   *  modified on HubSpot (proxy for "when the AE moved the close date"). */
  moved_at?: string | null;
  /** Lost bucket only: HubSpot ``closed_lost`` value (e.g. "Lost to No Decision"). */
  lost_reason?: string | null;
}

export interface SourceBucket {
  deal_source: string;
  deal_count: number;
  amount_cents: number;
}

export interface TofFunnel {
  new_companies: number;
  paid_companies: number;
  unpaid_companies: number;
  sqos: number;
  paid_conversion_pct: number | null;
}

export interface BdrOutbound {
  goal_cents: number;
  generated_cents: number;
  remaining_cents: number;
  pacing_pct: number;
}

export interface SegmentClosed {
  segment: string;
  closed_cents: number;
  forecast_cents?: number | null;
  upside_cents?: number | null;
}

/**
 * Three-bucket roll-up of closed revenue for the Closed tile's sub-numbers.
 * `total_cents` is the tile headline and always equals the New Revenue table's
 * subtotal — the BE emits both from one read so they can't disagree.
 */
/** One signed contract behind the Closed tile, for its drill-down drawer. */
export interface ClosedContractRow {
  contract_id: string | null;
  account_name: string | null;
  contract_type_name: string | null;
  segment: string;
  signed_date: string | null;
  closer_name: string | null;
  closer_team: string | null;
  hubspot_deal_id: string | null;
  /** mrr_total × 12 — what the contract is worth on paper. */
  face_revenue_cents: number;
  /** What actually counts toward Closed. Equals face except on replacements. */
  revenue_cents: number;
  /** face − counted. Non-zero only on replacements. */
  replacement_haircut_cents: number;
  prior_month_mrr_cents: number;
  subscription_mrr_cents: number;
}

/** One CRO call behind the Forecast tile. */
export interface ForecastDetailRow {
  rep_id: string | null;
  rep_name: string;
  tier: string | null;
  segment: string;
  month: number | null;
  call_cents: number;
  iso_year: number;
  iso_week: number;
}

export interface ClosedRollup {
  /** Direct NL — new logo. */
  new_cents: number;
  /** Direct Reseller + Upsell Reseller. */
  partner_cents: number;
  /** Upsell & Replacement — CS expansion. Replacements are counted at their
   *  like-for-like delta, so this can be negative (contraction). */
  cs_cents: number;
  total_cents: number;
}

export interface PlanForecastActualPoint {
  iso_year: number;
  iso_week?: number | null;
  label: string;
  value_cents: number;
}

/** Indicates how the payload was sourced — see backend service.py. */
export type LockStatus = "live" | "frozen" | "locked" | "past_unlocked";

export interface DashboardPayload {
  iso_year: number;
  iso_week: number;
  prior_iso_year: number | null;
  prior_iso_week: number | null;
  banner: PipelineBannerData;
  /** HubSpot "Reseller Deals" — shown as the Partner Pipeline tile trio. */
  partner_banner: PipelineBannerData;
  /** HubSpot "Customer Success" — upsells & replacements tile trio. */
  cs_banner: PipelineBannerData;
  open_deals: OpenDeal[];
  pipeline_by_source: SourceBucket[];
  tof: TofFunnel | null;
  bdr: BdrOutbound | null;
  closed_by_segment_month: SegmentClosed[];
  closed_by_segment_quarter: SegmentClosed[];
  closed_rollup_month: ClosedRollup;
  closed_rollup_quarter: ClosedRollup;
  /** Per-contract constituents for the Closed tile drill-down. */
  closed_contracts_month: ClosedContractRow[];
  closed_contracts_quarter: ClosedContractRow[];
  cs_forecast: unknown | null;
  plan_vs_call: PlanForecastActualPoint[];
  notes: Record<string, string>;
  captured_at: string | null;
  lock_status?: LockStatus;
}

export interface SalesRep {
  id: string;
  hubspot_owner_id: string;
  display_name: string;
  email: string | null;
  tier: SalesRepTier;
  is_active: boolean;
  sort_order: number;
}

/**
 * Mirrors the `ck_sales_reps_tier` check constraint. `cro` carries no quota and
 * is excluded from the new-revenue segment breakout — it exists so the CRO stops
 * being filed under `inactive`, which is reserved for genuinely deactivated reps.
 * `smb_se` is retained for historical rows.
 */
export type SalesRepTier =
  | "enterprise"
  | "smb_ae"
  | "smb_se"
  | "sales_engineer"
  | "partner"
  | "cs"
  | "cro"
  | "inactive";

export interface DealCommit {
  id: string;
  hubspot_deal_id: string;
  rep_id: string | null;
  forecast_year: number;
  forecast_month: number;
  probability: number;
  commit_category: string | null;
  is_strategic: boolean;
  note: string | null;
}

export interface WeeklyCallUpsert {
  iso_year: number;
  iso_week: number;
  rep_id: string | null;
  target_period_type: "month" | "quarter";
  target_year: number;
  target_period: number;
  call_cents: number;
  reasoning?: string | null;
}

// ── Hooks ───────────────────────────────────────────────────────────────

export function useDashboard(params: {
  year?: number;
  week?: number;
  pipeline?: string;
}) {
  const qs = new URLSearchParams();
  if (params.year) qs.set("year", String(params.year));
  if (params.week) qs.set("week", String(params.week));
  if (params.pipeline) qs.set("pipeline", params.pipeline);
  const query = qs.toString();
  return useQuery({
    queryKey: ["cro_forecast", "dashboard", params],
    queryFn: () =>
      apiFetch<DashboardPayload>(`/cro_forecast/dashboard${query ? `?${query}` : ""}`),
    staleTime: 30_000,
  });
}

export function useSalesReps() {
  return useQuery({
    queryKey: ["cro_forecast", "reps"],
    queryFn: () => apiFetch<SalesRep[]>("/cro_forecast/reps"),
    staleTime: 5 * 60_000,
  });
}

export function useUpsertDealCommit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      hubspot_deal_id: string;
      rep_id?: string | null;
      forecast_year: number;
      forecast_month: number;
      probability: number;
      commit_category?: string | null;
      is_strategic?: boolean;
      note?: string | null;
    }) =>
      apiFetch<DealCommit>(`/cro_forecast/deals/${payload.hubspot_deal_id}/commit`, {
        method: "PUT",
        body: payload,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cro_forecast", "dashboard"] });
    },
  });
}

export function useUpsertWeeklyCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: WeeklyCallUpsert) =>
      apiFetch(`/cro_forecast/weekly-call`, { method: "PUT", body: payload }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cro_forecast", "dashboard"] });
    },
  });
}

export interface SnapshotRead {
  id: string;
  iso_year: number;
  iso_week: number;
  captured_at: string;
  trigger: "cron_monday" | "cron_month_final" | "manual";
  is_month_final: boolean;
  is_locked: boolean;
  fiscal_year_month: string | null;
  payload: unknown;
}

/** Fetch the latest snapshot row for a week (or null if none yet captured).
 *  Used by the FilterBar to render the Lock button's current state. */
export function useSnapshot(year: number, week: number) {
  return useQuery({
    queryKey: ["cro_forecast", "snapshot", year, week],
    queryFn: async () => {
      try {
        return await apiFetch<SnapshotRead | null>(
          `/cro_forecast/snapshot/${year}/${week}`,
        );
      } catch (err) {
        // 404 = no snapshot yet — that's a valid "not captured" state.
        if ((err as { status?: number }).status === 404) return null;
        throw err;
      }
    },
    staleTime: 30_000,
  });
}

export function useLockSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { year: number; week: number }) =>
      apiFetch<SnapshotRead>(
        `/cro_forecast/snapshot/${payload.year}/${payload.week}/lock`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cro_forecast"] });
    },
  });
}

export function useUnlockSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { year: number; week: number }) =>
      apiFetch<SnapshotRead>(
        `/cro_forecast/snapshot/${payload.year}/${payload.week}/unlock`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cro_forecast"] });
    },
  });
}

export function useCaptureSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/cro_forecast/snapshot`, {
        method: "POST",
        body: { trigger: "manual" },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cro_forecast"] });
    },
  });
}

// ── Phase 2 types + hooks ─────────────────────────────────────────────────

export interface SummaryPayload extends DashboardPayload {
  quarter_year: number;
  quarter: number;
  fiscal_month: number;
  /**
   * Week-over-week deltas for every top card, keyed by the BE's flattened
   * names. A key is absent or null when the comparison isn't valid: no prior
   * snapshot, a card that didn't exist last week, or a to-date figure whose
   * month/quarter has since reset.
   *
   *   banner.total | banner.deals | banner.avg
   *   stage.<Stage Name>
   *   partner.total | partner.deals | partner.avg
   *   cs.total | cs.deals | cs.avg
   *   mtd.closed | mtd.forecast | mtd.upside
   *   qtd.closed | qtd.forecast | qtd.upside
   *
   * Money values are cents; `.deals` are counts.
   */
  wow: Record<string, number | null>;
  forecast_by_segment_month: SegmentClosed[];
  forecast_by_segment_quarter: SegmentClosed[];
  /** Per-rep CRO calls behind the Forecast tile, for its drill-down. */
  forecast_detail_month: ForecastDetailRow[];
  forecast_detail_quarter: ForecastDetailRow[];
  upside_by_segment_month: SegmentClosed[];
  upside_by_segment_quarter: SegmentClosed[];
  churn_month_cents: number;
  churn_quarter_cents: number;
  churn_forecast_month_cents: number;
  churn_forecast_quarter_cents: number;
  plan_month_cents: number;
  plan_quarter_cents: number;
  plan_min_month_cents: number | null;
  plan_max_month_cents: number | null;
  plan_min_quarter_cents: number | null;
  plan_max_quarter_cents: number | null;
  llm_commentary: LlmCommentary | null;
}

export interface LlmCommentary {
  id: string;
  fiscal_year: number;
  period_type: "month" | "quarter";
  period_value: number;
  scope: string;
  prompt_version: string;
  model_name: string | null;
  body: string;
  generated_at: string;
}

export interface PipelineMovementBucket {
  bucket: "closed_won" | "advanced" | "new_pipeline" | "lost" | "pushed";
  deals: OpenDeal[];
  deal_count: number;
  total_amount_cents: number;
}

export interface PipelineMovementPayload {
  iso_year: number;
  iso_week: number;
  week_start: string;
  week_end_exclusive: string;
  buckets: PipelineMovementBucket[];
  lock_status?: LockStatus;
}

export interface BdrPerson {
  hubspot_owner_id: string;
  display_name: string;
  /** Deprecated — Phase 2 single-period rows. Phase 4 uses the per-period fields below. */
  deals_created?: number;
  pipeline_added_cents?: number;
  /** Phase 4 per-period split. */
  deals_week?: number;
  deals_month?: number;
  deals_quarter?: number;
  pipeline_week_cents?: number;
  pipeline_month_cents?: number;
  pipeline_quarter_cents?: number;
}

export interface WeeklyKpiRow {
  week_start: string;
  sqo_count: number;
  pipeline_added_cents: number;
  new_companies: number;
  closed_won_cents: number;
}

export interface TofPayload {
  iso_year: number;
  iso_week: number;
  fiscal_month: number;
  quarter: number;
  funnel: TofFunnel | null;
  funnel_week: TofFunnel | null;
  funnel_month: TofFunnel | null;
  funnel_quarter: TofFunnel | null;
  weekly_new_companies: WeeklyKpiRow[];
  bdr_team: BdrPerson[];
  source_breakdown: SourceBucket[];
  lock_status?: LockStatus;
}

export interface AePacingWeek {
  week_start: string;
  pipeline_added_cents: number;
  sqo_count: number;
  cumulative_cents: number;
  ideal_cents: number;
  pct_to_pace: number | null;
}

export interface AePacingRow {
  rep_id: string;
  display_name: string;
  tier: string;
  hubspot_owner_id: string;
  annual_quota_cents: number;
  annual_pipeline_target_cents: number;
  weekly_pace_target_cents: number;
  weeks: AePacingWeek[];
  cumulative_cents: number;
}

export interface WeeklyKpiMovementSummary {
  cltnd_count: number;
  cltnd_cents: number;
  cltc_count: number;
  cltc_cents: number;
  pushed_count: number;
  pushed_cents: number;
  new_pipeline_count: number;
  new_pipeline_cents: number;
  advanced_count: number;
  advanced_cents: number;
}

export interface WeeklyKpiPayload {
  iso_year: number;
  iso_week: number;
  fiscal_year: number;
  quarter: number;
  rows: WeeklyKpiRow[];
  per_ae: AePacingRow[];
  movement?: WeeklyKpiMovementSummary | null;
  lock_status?: LockStatus;
}

export interface AeSubmissionHistoryEntry {
  iso_year: number;
  iso_week: number;
  call_cents: number;
  source: string;
  reasoning: string | null;
  created_at: string | null;
}

export interface AeForecastRow {
  rep_id: string;
  display_name: string;
  tier: string;
  months: {
    month: number;
    call_cents: number;
    closed_cents: number;
    pipeline_cents: number;
    /** AE's HubSpot Forecast Submission for the month (sum of
     *  hs_forecast_amount || hs_projected_amount across their deals
     *  closing that month). Used as fallback pre-fill when no locked
     *  weekly_ae_calls row exists yet. */
    hubspot_submission_cents?: number;
    /** The most recently saved weekly_ae_calls value for (rep, month)
     *  — the locked, point-in-time AE submission. Null when no AE row
     *  exists yet; the drawer falls back to hubspot_submission_cents. */
    submitted_cents?: number | null;
    /** ISO label of the week the most recent submission was made
     *  (e.g. "2026-W26"). */
    submitted_at_iso_week?: string | null;
    /** AE's reasoning text on the latest submission. */
    submitted_reasoning?: string | null;
    /** All historic weekly submissions for (rep, month). */
    submission_history?: AeSubmissionHistoryEntry[];
    /** submitted_cents ?? hubspot_submission_cents — the value in force. */
    effective_submission_cents?: number;
    /** What this month's submission read as of the prior ISO week. Null when
     *  the AE hadn't submitted yet, so the FE shows no movement badge. */
    prior_week_submission_cents?: number | null;
  }[];
  quarter_call_cents: number;
  quarter_pipeline_cents: number;
  /** Open deals whose HubSpot close date falls INSIDE the viewed quarter —
   *  the coverage numerator. Narrower than quarter_pipeline_cents, which is
   *  the rep's entire open book regardless of close date. */
  quarter_close_pipeline_cents: number;
  /** Every month of the quarter the AE has called, summed. */
  quarter_submission_cents: number;
  /**
   * Current month's submission minus what last week's snapshot of this table
   * showed. Null when there's nothing comparable — no snapshot for the prior
   * week, or the rep wasn't on the roster then — so the FE renders no chip
   * rather than a fake movement.
   */
  submission_wow_delta_cents: number | null;
  /**
   * Same comparison for the quarter column. Additionally null for weeks whose
   * snapshot predates `quarter_submission_cents` (it lands from 2026-W32 on).
   */
  quarter_submission_wow_delta_cents: number | null;
  /** Forecast Submission for the *current fiscal month* (the month
   *  containing the viewed iso-week). Picks the locked weekly_ae_calls
   *  row when present, else falls back to HubSpot's projected value.
   *  Matches what HubSpot's Forecasting view shows for the month. */
  month_submission_cents: number;
  quarter_coverage: number | null;
  open_deals: OpenDeal[];
  /** Deals the AE explicitly included in their CURRENT-MONTH Forecast
   *  Submission in HubSpot. Subset of ``open_deals`` plus any deals the
   *  AE forecasted that have since closed (those carry minimal stub
   *  fields with name="(closed or unavailable)"). */
  forecast_deals: OpenDeal[];
  notes: string | null;
}

export interface ItemOfBusiness {
  id: string;
  rep_id: string | null;
  hubspot_deal_id: string | null;
  item_type: string;
  title: string;
  detail: string | null;
  resolved: boolean;
}

export interface ForecastMeetingPayload {
  iso_year: number;
  iso_week: number;
  quarter_year: number;
  quarter: number;
  /** Fiscal month of the viewed iso-week (1–12). Used by the FE to
   *  label the "Forecast Submission" column with the right month. */
  fiscal_month: number;
  months: number[];
  reps: AeForecastRow[];
  agenda_markdown: string | null;
  meeting_summary_markdown: string | null;
  items_of_business: ItemOfBusiness[];
  lock_status?: LockStatus;
}

function makeQS(params: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== "") qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export function useSummary(year?: number, week?: number) {
  return useQuery({
    queryKey: ["cro_forecast", "summary", year, week],
    queryFn: () =>
      apiFetch<SummaryPayload>(
        `/cro_forecast/summary${makeQS({ year, week })}`,
      ),
    staleTime: 30_000,
  });
}

export function useWeeklyKpi(year?: number, week?: number) {
  return useQuery({
    queryKey: ["cro_forecast", "weekly-kpi", year, week],
    queryFn: () =>
      apiFetch<WeeklyKpiPayload>(
        `/cro_forecast/weekly-kpi${makeQS({ year, week })}`,
      ),
    staleTime: 30_000,
  });
}

export function useForecastMeeting(year?: number, week?: number) {
  return useQuery({
    queryKey: ["cro_forecast", "forecast-meeting", year, week],
    queryFn: () =>
      apiFetch<ForecastMeetingPayload>(
        `/cro_forecast/forecast-meeting${makeQS({ year, week })}`,
      ),
    staleTime: 30_000,
  });
}

/** All three filters are multi-select — each as a repeatable query
 *  param (?pipeline=...&pipeline=..., etc). Empty arrays are dropped. */
export interface PipelineMovementFilters {
  pipelines?: string[];
  stages?: string[];
  /** HubSpot owner ids (the rep dropdown values). */
  reps?: string[];
}

export function usePipelineMovement(
  year?: number,
  week?: number,
  filters?: PipelineMovementFilters,
) {
  const qs = new URLSearchParams();
  if (year != null) qs.set("year", String(year));
  if (week != null) qs.set("week", String(week));
  for (const p of filters?.pipelines ?? []) qs.append("pipeline", p);
  for (const s of filters?.stages ?? []) qs.append("stage", s);
  for (const r of filters?.reps ?? []) qs.append("rep", r);
  const qsStr = qs.toString();
  return useQuery({
    queryKey: [
      "cro_forecast",
      "pipeline-movement",
      year,
      week,
      (filters?.pipelines ?? []).join(","),
      (filters?.stages ?? []).join(","),
      (filters?.reps ?? []).join(","),
    ],
    queryFn: () =>
      apiFetch<PipelineMovementPayload>(
        `/cro_forecast/pipeline-movement${qsStr ? `?${qsStr}` : ""}`,
      ),
    staleTime: 30_000,
  });
}

export function useTopOfFunnel(year?: number, week?: number) {
  return useQuery({
    queryKey: ["cro_forecast", "top-of-funnel", year, week],
    queryFn: () =>
      apiFetch<TofPayload>(
        `/cro_forecast/top-of-funnel${makeQS({ year, week })}`,
      ),
    staleTime: 30_000,
  });
}

export interface HubspotForecastStage {
  stage_id: string;
  stage_label: string;
  stage_close_weighting: number;
  deal_count: number;
  amount_cents: number;
  projected_amount_cents: number;
}

export interface HubspotForecastPayload {
  pipeline_id?: string;
  pipeline_label?: string;
  period_start?: string;
  period_end_exclusive?: string;
  by_stage: HubspotForecastStage[];
  totals: {
    deal_count: number;
    amount_cents: number;
    projected_amount_cents: number;
  };
  by_forecast_category: Record<string, number>;
  fetched_at?: string;
  not_configured?: boolean;
  detail?: string;
}

export function useHubspotForecast(params: {
  fiscalYear: number;
  periodType: "month" | "quarter";
  periodValue: number;
  pipeline?: string;
  enabled?: boolean;
}) {
  const qs = new URLSearchParams({
    fiscal_year: String(params.fiscalYear),
    period_type: params.periodType,
    period_value: String(params.periodValue),
    pipeline: params.pipeline ?? "default",
  });
  return useQuery({
    queryKey: [
      "cro_forecast",
      "hubspot-forecast",
      params.fiscalYear,
      params.periodType,
      params.periodValue,
      params.pipeline ?? "default",
    ],
    queryFn: () =>
      apiFetch<HubspotForecastPayload>(
        `/cro_forecast/hubspot-forecast?${qs.toString()}`,
      ),
    staleTime: 5 * 60_000, // 5 min — HubSpot rate limits favor caching
    enabled: params.enabled ?? false,
  });
}

export interface KpiHistorySeriesPoint {
  iso_year: number;
  iso_week: number;
  value_cents: number;
}

export interface KpiHistoryPayload {
  weeks: { iso_year: number; iso_week: number }[];
  series: Record<string, KpiHistorySeriesPoint[]>;
}

export function useKpiHistory(weeks: number = 12) {
  return useQuery({
    queryKey: ["cro_forecast", "kpi-history", weeks],
    queryFn: () =>
      apiFetch<KpiHistoryPayload>(`/cro_forecast/kpi-history?weeks=${weeks}`),
    staleTime: 60_000,
  });
}

export interface AeDetailPayload {
  rep_id: string;
  display_name: string;
  email: string | null;
  tier: string;
  hubspot_owner_id: string;
  iso_year: number;
  iso_week: number;
  quarter_year: number;
  quarter: number;
  months: { month: number; call_cents: number; closed_cents: number }[];
  trajectory: { iso_year: number; iso_week: number; cents: number }[];
  overrides: {
    iso_year: number;
    iso_week: number;
    override_pct: number;
    note: string | null;
  }[];
  open_deals: OpenDeal[];
}

export function useAeDetail(repId: string, year?: number, week?: number) {
  return useQuery({
    queryKey: ["cro_forecast", "ae", repId, year, week],
    queryFn: () =>
      apiFetch<AeDetailPayload>(
        `/cro_forecast/ae/${repId}${makeQS({ year, week })}`,
      ),
    staleTime: 30_000,
    enabled: !!repId,
  });
}

export function useGenerateCommentary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      fiscal_year: number;
      period_type: "month" | "quarter";
      period_value: number;
      scope?: string;
      force?: boolean;
      year?: number;
      week?: number;
    }) => {
      const { year, week, ...body } = payload;
      return apiFetch<LlmCommentary>(
        `/cro_forecast/commentary${makeQS({ year, week })}`,
        { method: "POST", body },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cro_forecast", "summary"] });
    },
  });
}

export function useUpsertNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      iso_year: number;
      iso_week: number;
      scope: string;
      body: string;
    }) =>
      apiFetch(`/cro_forecast/notes`, { method: "PUT", body: payload }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cro_forecast"] });
    },
  });
}

// ── Reconcile Deals (HubSpot closed-won ↔ Cinch billing_contracts) ───────

export interface ReconcileHubspotOnly {
  hubspot_deal_id: string;
  name: string;
  pipeline_name: string | null;
  stage_name: string | null;
  amount_cents: number;
  hubspot_owner_id: string | null;
  owner_display_name: string | null;
  closed_at: string | null;
}

export interface ReconcileCinchOnly {
  contract_id: string;
  contract_type: string | null;
  account_id: string | null;
  hubspot_deal_id_on_contract: string | null;
  revenue_cents: number;
  closer_hubspot_owner_id: string | null;
  signed_at: string | null;
}

export interface ReconcileMismatchContract {
  contract_id: string;
  contract_type: string | null;
  revenue_cents: number;
  signed_at: string | null;
}

export interface ReconcileMismatch {
  hubspot_deal_id: string;
  name: string;
  contracts: ReconcileMismatchContract[];
  deal_amount_cents: number;
  contract_revenue_cents: number; // sum across contracts
  delta_cents: number; // contracts_total - deal; positive = Cinch is higher
  hubspot_owner_id: string | null;
  owner_display_name: string | null;
  closed_at: string | null;
}

export interface ReconciliationPayload {
  fiscal_year: number;
  fiscal_month: number;
  period_start: string;
  period_end: string;
  unmatched_hubspot: ReconcileHubspotOnly[];
  unmatched_cinch: ReconcileCinchOnly[];
  mismatched_amounts: ReconcileMismatch[];
}

export function useReconciliation(year: number, month: number) {
  return useQuery({
    queryKey: ["cro_forecast", "reconcile", year, month],
    queryFn: () =>
      apiFetch<ReconciliationPayload>(
        `/cro_forecast/reconcile?year=${year}&month=${month}`,
      ),
  });
}

// ── Deal Health ───────────────────────────────────────────────────────────

export type DealHealthBand = "healthy" | "at_risk" | "stalled";

export interface DealHealthScores {
  engagement: number;
  deal_quality: number;
  urgency: number;
  momentum: number;
  multithreading: number;
}

export interface DealHealthDeal {
  hubspot_deal_id: string;
  name: string;
  pipeline_name: string | null;
  company_id: string | null;
  company_name: string | null;
  stage_name: string | null;
  amount_cents: number;
  /** ISO timestamp the deal was opened/created in HubSpot. */
  created_at: string | null;
  close_date: string | null;
  deal_source: string | null;
  competitor: string | null;
  owner_display_name: string | null;
  next_step: string | null;
  next_step_updated_on: string | null;
  contact_count: number;
  emails_90d: number;
  inbound_emails_90d: number;
  calls_90d: number;
  completed_meetings: number;
  no_shows: number;
  days_since_touch: number | null;
  days_in_stage: number | null;
  days_to_close: number | null;
  health_score: number;
  band: DealHealthBand;
  band_reasons: string[];
  scores: DealHealthScores;
}

export interface DealHealthPayload {
  /** Every pipeline covered by the payload (deals carry their own). */
  pipelines: string[];
  /**
   * Stage names per pipeline in FUNNEL order (the BE's stage ladders), with any
   * stage seen in the data but not yet codified appended. Drives the stage
   * filter's ordering — alphabetical would scramble the ladder.
   */
  stages_by_pipeline: Record<string, string[]>;
  fetched_at: string | null;
  /**
   * Newest HubSpot deal edit the nightly mart has captured (UTC ISO). Anything
   * changed in HubSpot after this isn't reflected yet, even after Refresh.
   */
  data_as_of: string | null;
  summary: {
    open_deals: number;
    pipeline_cents: number;
    healthy: number;
    at_risk: number;
    stalled: number;
  };
  deals: DealHealthDeal[];
}

export function useDealHealth() {
  return useQuery({
    queryKey: ["cro_forecast", "deal-health"],
    queryFn: () => apiFetch<DealHealthPayload>(`/cro_forecast/deal-health`),
    staleTime: 5 * 60_000, // raw rows are sf_cached server-side anyway
  });
}

/** The tab's Refresh button: bypasses the server-side sf_cache (fresh
 *  Snowflake pull) and swaps the result into the existing query cache. */
export function useRefreshDealHealth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<DealHealthPayload>(`/cro_forecast/deal-health?refresh=true`),
    onSuccess: (data) => {
      qc.setQueryData(["cro_forecast", "deal-health"], data);
    },
  });
}
